import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useExperience } from '../ExperienceProvider';

// The §8.6 consent & control panel. Calmest defaults; "off is as easy as on". Every
// control writes a single field through updatePrefs (optimistic + backend persist). All
// strings via t(). Light ivory/champagne lane; no dark chrome, no fake metrics.

function Toggle({ id, checked, onChange, label, help }) {
  const { t } = useLanguage();
  return (
    <div className="flex items-start justify-between gap-4 border-b border-champagne-100 py-3">
      <div className="min-w-0">
        <span id={`${id}-label`} className="block text-sm font-medium text-slate-800">{label}</span>
        {help && <p className="mt-0.5 text-xs text-slate-500">{help}</p>}
      </div>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-signal-blue' : 'bg-slate-300'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
        <span className="sr-only">
          {checked ? t('experience.settings.on', 'On') : t('experience.settings.off', 'Off')}
        </span>
      </button>
    </div>
  );
}

function ChoiceRow({ id, value, onChange, label, help, options }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-champagne-100 py-3">
      <div className="min-w-0">
        <label htmlFor={id} className="block text-sm font-medium text-slate-800">{label}</label>
        {help && <p className="mt-0.5 text-xs text-slate-500">{help}</p>}
      </div>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="shrink-0 rounded-md border border-champagne-200 bg-white px-2 py-1 text-sm text-slate-800"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

export default function ExperienceSettings() {
  const { t } = useLanguage();
  const { prefs, updatePrefs } = useExperience();
  const set = (patch) => updatePrefs(patch);
  const consentOn = !!(prefs.consent && prefs.consent.experience_data);

  return (
    <section
      data-testid="settings-experience-section"
      className="rounded-2xl border border-champagne-200 bg-white p-6"
    >
      <h2 className="font-heading text-xl text-slate-900">
        {t('experience.settings.title', 'Experience & accessibility')}
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        {t('experience.settings.intro', 'Control how this app moves, sounds, and celebrates. Everything is calm by default, and turning something off is as easy as turning it on.')}
      </p>

      <div className="mt-4">
        <Toggle
          id="xp-enable"
          checked={!!prefs.enabled}
          onChange={(v) => set({ enabled: v })}
          label={t('experience.settings.enable_label', 'Enable experience features')}
          help={t('experience.settings.enable_help', 'Record milestones and personalized moments. Off by default.')}
        />
        <ChoiceRow
          id="xp-mode"
          value={prefs.mode || 'Balanced'}
          onChange={(v) => set({ mode: v })}
          label={t('experience.settings.mode_label', 'Intensity')}
          help={t('experience.settings.mode_help', 'How present the experience is. Balanced is the default.')}
          options={[
            { value: 'Minimal', label: t('experience.settings.mode_minimal', 'Minimal') },
            { value: 'Balanced', label: t('experience.settings.mode_balanced', 'Balanced') },
            { value: 'Celebration', label: t('experience.settings.mode_celebration', 'Celebration') },
          ]}
        />
        <ChoiceRow
          id="xp-motion"
          value={prefs.reducedMotion || 'system'}
          onChange={(v) => set({ reducedMotion: v })}
          label={t('experience.settings.motion_label', 'Motion')}
          help={t('experience.settings.motion_help', "Your device's reduce-motion setting is always respected.")}
          options={[
            { value: 'system', label: t('experience.settings.motion_system', 'Follow system') },
            { value: 'on', label: t('experience.settings.motion_on', 'Reduce motion') },
            { value: 'off', label: t('experience.settings.motion_off', 'Allow motion') },
          ]}
        />
        <Toggle
          id="xp-sound"
          checked={!!prefs.sound}
          onChange={(v) => set({ sound: v })}
          label={t('experience.settings.sound_label', 'Sound')}
          help={t('experience.settings.sound_help', 'Play a short cue on milestones. Off by default; never autoplays.')}
        />
        <Toggle
          id="xp-quiet"
          checked={!!prefs.quietMode}
          onChange={(v) => set({ quietMode: v })}
          label={t('experience.settings.quiet_label', 'Quiet mode')}
          help={t('experience.settings.quiet_help', 'Silences sound, reduces motion, and calms non-critical notifications everywhere.')}
        />
        <Toggle
          id="xp-notifs"
          checked={prefs.notifications !== false}
          onChange={(v) => set({ notifications: v })}
          label={t('experience.settings.notifications_label', 'Account notifications')}
          help={t('experience.settings.notifications_help', 'Important account and booking updates.')}
        />
        <Toggle
          id="xp-celeb-notifs"
          checked={!!prefs.celebrationNotifications}
          onChange={(v) => set({ celebrationNotifications: v })}
          label={t('experience.settings.celebration_notifications_label', 'Celebration notifications')}
          help={t('experience.settings.celebration_notifications_help', 'Optional notifications for milestones. Off by default.')}
        />
        <Toggle
          id="xp-consent"
          checked={consentOn}
          onChange={(v) => set({ consent: { experience_data: v } })}
          label={t('experience.settings.consent_label', 'Record my experience data')}
          help={t('experience.settings.consent_help', 'Allow this app to record your milestones and history. You can erase it at any time.')}
        />
      </div>
    </section>
  );
}
